import exifr from 'exifr';

type ExifCoordinates = {
    latitude: number,
    longitude: number
};

type ExifImageSize = {
    width: number,
    height: number
};

type ExtractedExifMetadata = {
    capturedAt?: string,
    device?: string,
    gps?: ExifCoordinates,
    imageSize?: ExifImageSize
};

const toFiniteNumber = (value: unknown) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return undefined;
    }
    return value;
};

const toNormalizedText = (value: unknown) => {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

const extractFirst = (value: Record<string, unknown>, keys: string[]) => {
    for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
            return value[key];
        }
    }
    return undefined;
};

const normalizeExifDate = (value: unknown) => {
    if (value instanceof Date && Number.isFinite(value.getTime())) {
        return value.toISOString();
    }

    const text = toNormalizedText(value);
    if (!text) {
        return undefined;
    }

    const exifTimestamp = text.match(/^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
    if (exifTimestamp) {
        const year = Number.parseInt(exifTimestamp[1], 10);
        const month = Number.parseInt(exifTimestamp[2], 10);
        const day = Number.parseInt(exifTimestamp[3], 10);
        const hour = Number.parseInt(exifTimestamp[4], 10);
        const minute = Number.parseInt(exifTimestamp[5], 10);
        const second = Number.parseInt(exifTimestamp[6], 10);
        const utcTime = Date.UTC(year, month - 1, day, hour, minute, second);
        if (Number.isFinite(utcTime)) {
            return new Date(utcTime).toISOString();
        }
    }

    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString();
    }

    return undefined;
};

const normalizeDevice = (makeValue: unknown, modelValue: unknown) => {
    const make = toNormalizedText(makeValue);
    const model = toNormalizedText(modelValue);
    if (!make && !model) {
        return undefined;
    }
    if (!make) {
        return model;
    }
    if (!model) {
        return make;
    }

    const lowerMake = make.toLowerCase();
    const lowerModel = model.toLowerCase();
    if (lowerModel.startsWith(lowerMake)) {
        return model;
    }

    return `${make} ${model}`;
};

const extractExifMetadata = async (file: File): Promise<ExtractedExifMetadata | null> => {
    try {
        const tags = await exifr.parse(file, [
            'DateTimeOriginal',
            'CreateDate',
            'ModifyDate',
            'DateTime',
            'Make',
            'Model',
            'latitude',
            'GPSLatitude',
            'longitude',
            'GPSLongitude',
            'ImageWidth',
            'ExifImageWidth',
            'PixelXDimension',
            'ImageHeight',
            'ExifImageHeight',
            'PixelYDimension'
        ]) as Record<string, unknown> | null;

        if (!tags || typeof tags !== 'object') {
            return null;
        }

        const capturedAt = normalizeExifDate(extractFirst(tags, ['DateTimeOriginal', 'CreateDate', 'ModifyDate', 'DateTime']));
        const device = normalizeDevice(extractFirst(tags, ['Make']), extractFirst(tags, ['Model']));

        const latitude = toFiniteNumber(extractFirst(tags, ['latitude', 'GPSLatitude']));
        const longitude = toFiniteNumber(extractFirst(tags, ['longitude', 'GPSLongitude']));

        const width = toFiniteNumber(extractFirst(tags, ['ImageWidth', 'ExifImageWidth', 'PixelXDimension']));
        const height = toFiniteNumber(extractFirst(tags, ['ImageHeight', 'ExifImageHeight', 'PixelYDimension']));

        const gps = (latitude !== undefined && longitude !== undefined) ? { latitude, longitude } : undefined;
        const imageSize = (width !== undefined && height !== undefined) ? { width, height } : undefined;

        if (!capturedAt && !device && !gps && !imageSize) {
            return null;
        }

        return {
            capturedAt,
            device,
            gps,
            imageSize
        };
    } catch {
        return null;
    }
};

export { extractExifMetadata };
export const exifReaderTestables = {
    normalizeExifDate
};
export type { ExtractedExifMetadata };
